import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';

import { isNativeMobile } from './nativeBridge';
import { nativeTapFeedback } from './nativeShell';
import { pullToRefreshHeld } from './pullToRefreshHold';

/** Finger travel is damped by half, so the threshold needs a deliberate ~130px pull. */
export const PULL_REFRESH_THRESHOLD = 64;
const MAX_PULL = 96;
const RESISTANCE = 0.5;
// Finger travel before deciding between a pull and a sideways swipe.
const DIRECTION_SLOP = 10;
// Meeting calls own their media and connection state; a reload would drop the call.
const BLOCKED_PATH_PREFIXES = ['/meetings/'];
// Drag handles (@hello-pangea/dnd) reorder lists by dragging down; a reload would race the save.
const EDITABLE_SELECTOR = 'input, textarea, select, [contenteditable=""], [contenteditable="true"], [data-no-pull-refresh], [data-rfd-drag-handle-draggable-id]';

const pageScroller = () => document.getElementById('root') || document.scrollingElement;

/** True when the touch starts inside a nested scroller that is not at its top. */
function insideScrolledRegion(target: EventTarget | null, scroller: Element | null) {
  let node = target instanceof Element ? target : null;
  while (node && node !== scroller && node !== document.body) {
    if (node.scrollTop > 0) {
      const { overflowY } = window.getComputedStyle(node);
      if (overflowY === 'auto' || overflowY === 'scroll') return true;
    }
    node = node.parentElement;
  }
  return false;
}

type NativePullToRefreshProps = { onRefresh?: () => void };

/**
 * Pull down from the top of a page in the mobile app to reload it. The app scrolls
 * inside `#root` rather than the document, so neither the WebView nor Android's
 * SwipeRefreshLayout can tell when the page is at its top; this follows the real scroller.
 */
export function NativePullToRefresh({ onRefresh = () => window.location.reload() }: NativePullToRefreshProps) {
  const { pathname } = useLocation();
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;
  // Routes match case-insensitively, so /Meetings/<id> is still a call.
  const blocked = BLOCKED_PATH_PREFIXES.some((prefix) => pathname.toLowerCase().startsWith(prefix));

  // Navigating to a blocked route mid-pull drops the listeners; clear the half-drawn indicator too.
  useEffect(() => { if (blocked) setPull(0); }, [blocked]);

  useEffect(() => {
    if (!isNativeMobile() || blocked || refreshing) return undefined;
    let gesture: { x: number; y: number; armed: boolean } | null = null;
    let distance = 0;
    const reset = () => { gesture = null; distance = 0; setPull(0); };
    const start = (event: TouchEvent) => {
      const scroller = pageScroller();
      gesture = null;
      if (event.touches.length !== 1 || !scroller || scroller.scrollTop > 0) return;
      if (document.body.hasAttribute('data-scroll-locked')) return; // An open dialog or sheet.
      if (pullToRefreshHeld()) return; // A live call, voice session or recording.
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest(EDITABLE_SELECTOR) || insideScrolledRegion(target, scroller)) return;
      gesture = { x: event.touches[0].clientX, y: event.touches[0].clientY, armed: false };
    };
    const move = (event: TouchEvent) => {
      if (!gesture) return;
      const dx = event.touches[0].clientX - gesture.x;
      const dy = event.touches[0].clientY - gesture.y;
      if (distance === 0 && Math.abs(dx) < DIRECTION_SLOP && Math.abs(dy) < DIRECTION_SLOP) return;
      const scroller = pageScroller();
      // Sideways swipes and upward scrolls belong to the page, not to refresh.
      if (dy <= 0 || Math.abs(dx) > dy || (scroller && scroller.scrollTop > 0)) { reset(); return; }
      distance = Math.min(dy * RESISTANCE, MAX_PULL);
      const armed = distance >= PULL_REFRESH_THRESHOLD;
      if (armed && !gesture.armed) nativeTapFeedback();
      gesture.armed = armed;
      setPull(distance);
    };
    const end = () => {
      // A call, voice session or dialog may have started mid-pull.
      const stillAllowed = !pullToRefreshHeld() && !document.body.hasAttribute('data-scroll-locked');
      if (gesture?.armed && distance >= PULL_REFRESH_THRESHOLD && stillAllowed) {
        gesture = null;
        setRefreshing(true);
        setPull(PULL_REFRESH_THRESHOLD);
        onRefreshRef.current();
        return;
      }
      reset();
    };
    document.addEventListener('touchstart', start, { passive: true });
    document.addEventListener('touchmove', move, { passive: true });
    document.addEventListener('touchend', end);
    document.addEventListener('touchcancel', reset);
    return () => {
      document.removeEventListener('touchstart', start);
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', end);
      document.removeEventListener('touchcancel', reset);
    };
  }, [blocked, refreshing]);

  if (!pull && !refreshing) return null;
  const progress = Math.min(pull / PULL_REFRESH_THRESHOLD, 1);
  return (
    <div
      data-testid="native-pull-refresh"
      role={refreshing ? 'status' : undefined}
      aria-label={refreshing ? 'Refreshing' : undefined}
      className="pointer-events-none fixed left-1/2 z-[1000] flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#353947] shadow-md"
      style={{ top: 'calc(env(safe-area-inset-top) - 2.5rem)', transform: `translate(-50%, ${pull}px)`, opacity: refreshing ? 1 : 0.35 + progress * 0.65 }}
    >
      <RefreshCw aria-hidden="true" className={refreshing ? 'h-5 w-5 animate-spin' : 'h-5 w-5'} style={refreshing ? undefined : { transform: `rotate(${progress * 270}deg)` }} />
    </div>
  );
}
